const portfolio = [
  {
    icon: "fa-solid fa-solar-panel",
    eyebrow: "Solar farming",
    title: "Renewable Energy Projects",
    body: "Large-scale solar farms across Africa generating clean energy and competitive returns through long-term power purchase agreements.",
    metric: "12 – 15% target ROI",
    bullets: [
      "Land-based solar installations",
      "Long-term PPAs",
      "Proven panel & inverter tech",
      "Scalable build-out model",
    ],
    cta: { label: "Explore solar projects", href: "#contact" },
    image: "https://images.unsplash.com/photo-1559302504-64aae6ca6b6d?auto=format&fit=crop&w=900&q=80",
    accent: "amber",
  },
  {
    icon: "fa-solid fa-credit-card",
    eyebrow: "ParachPay",
    title: "Fintech & Money Transfer",
    body: "Send, receive and move money between people and merchants — built for Global and African markets with a mobile-first experience.",
    metric: "Growing user base",
    bullets: [
      "Cross-border transfers",
      "Low transaction fees",
      "Mobile-first wallet",
      "Merchant payments suite",
    ],
    cta: { label: "Visit ParachPay", href: "https://parachpay.com", external: true },
    image: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?auto=format&fit=crop&w=900&q=80",
    accent: "terracotta",
  },
  {
    icon: "fa-solid fa-graduation-cap",
    eyebrow: "Education",
    title: "AIGL Career Platform",
    body: "An online education platform for new careers — industry certifications, expert instructors and job placement support.",
    metric: "aiglnewcareer.com",
    bullets: [
      "Expert-led instructors",
      "Industry certifications",
      "Flexible self-paced learning",
      "Career & placement support",
    ],
    cta: { label: "Visit aiglnewcareer.com", href: "https://aiglnewcareer.com", external: true },
    image: "https://images.unsplash.com/photo-1571260899304-425eee4c7efc?auto=format&fit=crop&w=900&q=80",
    accent: "leaf",
  },
];

const accentMap = {
  amber: "text-[hsl(var(--amber))]",
  terracotta: "text-[hsl(var(--terracotta))]",
  leaf: "text-[hsl(var(--leaf))]",
};

export default function Portfolio() {
  return (
    <section id="portfolio" className="relative py-24 lg:py-32" data-testid="portfolio-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="eyebrow mb-5">Investment portfolio</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold leading-[1.05] text-[hsl(var(--espresso))]">
              Three pillars. <span className="italic">One</span> mission.
            </h2>
          </div>
          <p className="text-[hsl(var(--fg-muted))] max-w-md text-base leading-relaxed">
            Diversified opportunities across renewable energy, fintech and education — each
            designed to deliver sustainable returns alongside positive social impact.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {portfolio.map((p, i) => (
            <article
              key={p.title}
              className="edit-card overflow-hidden flex flex-col"
              data-testid={`portfolio-card-${i}`}
            >
              <div className="relative h-56 overflow-hidden">
                <img
                  src={p.image}
                  alt={p.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--espresso))]/70 via-transparent to-transparent" />
                <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-[hsl(var(--ivory))]/95 backdrop-blur rounded-full px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em]">
                  <i className={`${p.icon} ${accentMap[p.accent]}`}></i>
                  {p.eyebrow}
                </div>
                <div className="absolute bottom-4 left-4 right-4 text-white">
                  <div className="text-[11px] uppercase tracking-[0.22em] opacity-80">Highlight</div>
                  <div className="font-display text-lg font-semibold">{p.metric}</div>
                </div>
              </div>
              <div className="p-7 flex flex-col flex-grow">
                <h3 className="font-display text-2xl font-bold text-[hsl(var(--espresso))]">{p.title}</h3>
                <p className="mt-3 text-[hsl(var(--fg-muted))] leading-relaxed text-sm">{p.body}</p>
                <ul className="mt-5 space-y-2.5">
                  {p.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2.5 text-sm text-[hsl(var(--espresso))]">
                      <span className="w-5 h-5 rounded-full bg-[hsl(var(--amber-soft))] text-[hsl(var(--terracotta))] inline-flex items-center justify-center mt-0.5 shrink-0">
                        <i className="fa-solid fa-check text-[10px]"></i>
                      </span>
                      {b}
                    </li>
                  ))}
                </ul>
                <div className="mt-7 pt-5 border-t border-[hsl(var(--line))]">
                  {p.cta.external ? (
                    <a
                      href={p.cta.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 font-semibold text-[hsl(var(--terracotta))] hover:text-[hsl(var(--terracotta-deep))]"
                      data-testid={`portfolio-cta-${i}`}
                    >
                      {p.cta.label}
                      <i className="fa-solid fa-arrow-up-right-from-square text-xs"></i>
                    </a>
                  ) : (
                    <a
                      href={p.cta.href}
                      className="inline-flex items-center gap-2 font-semibold text-[hsl(var(--terracotta))] hover:text-[hsl(var(--terracotta-deep))]"
                      data-testid={`portfolio-cta-${i}`}
                    >
                      {p.cta.label}
                      <i className="fa-solid fa-arrow-right text-xs"></i>
                    </a>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
